import { Server } from 'mock-socket';
import configJson from './config.json';
import configNclJson from './config-ncl.json';
import { isOfflineNCLMode } from './utils';
import {
    buildConversationMap,
    setConversationMap,
    resetActiveReplayId,
    socketSessions,
    handleUserMessage
} from './aiChatSeeder';

const offlineConfig = isOfflineNCLMode() ? configNclJson : configJson;

// ============================================================================
// Server Lifecycle
// ============================================================================

window.aiChatServerInstance = null;

let uninstallUrlRewrite = null;

/**
 * Builds the URL the mock chat server listens on. The host is arbitrary — the
 * client is redirected onto this URL by installChatSocketUrlRewrite().
 * @param {string} endpointPath - WebSocket path from the offline config.
 * @returns {string}
 */
const getAiChatWebSocketUrl = (endpointPath) => {
    const { origin } = window.location;
    const protocol = origin.startsWith('https') ? 'wss:' : 'ws:';
    return `${protocol}//${origin.replace(/^https?:\/\//, '')}${endpointPath}`;
};

/**
 * Routes the chat client to the mock server whatever host it dials.
 *
 * mock-socket serves an exact URL match only, and the chat host comes from
 * externals.platformBackend — the live support-services response, which offline
 * is not knowable when the server is installed. Matching on the endpoint path
 * instead keeps the simulator working when that host changes; sockets on any
 * other path are passed through untouched.
 *
 * @param {string} serverUrl - URL the mock server listens on.
 * @param {string} endpointPath - Chat WebSocket path.
 * @returns {Function} Restores the previous WebSocket constructor.
 */
const installChatSocketUrlRewrite = (serverUrl, endpointPath) => {
    const OriginalWebSocket = window.WebSocket;
    if (typeof OriginalWebSocket !== 'function') return () => {};

    const isChatSocket = (url) => {
        try {
            return new URL(String(url), window.location.origin).pathname === endpointPath;
        } catch (_e) {
            return false;
        }
    };

    // A plain constructor rather than a subclass: mock-socket's WebSocket is an
    // ES5 function, and returning its instance keeps `instanceof` intact via the
    // shared prototype.
    const RewritingWebSocket = function (url, protocols) {
        return new OriginalWebSocket(isChatSocket(url) ? serverUrl : url, protocols);
    };
    RewritingWebSocket.prototype = OriginalWebSocket.prototype;
    ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'].forEach((state) => {
        RewritingWebSocket[state] = OriginalWebSocket[state];
    });

    window.WebSocket = RewritingWebSocket;

    return () => {
        if (window.WebSocket === RewritingWebSocket) {
            window.WebSocket = OriginalWebSocket;
        }
    };
};

/**
 * Creates a mock WebSocket server that replays recorded conversations.
 * On receiving a user prompt, it matches it to the closest recorded conversation
 * and replays the corresponding server responses, supporting multi-turn dialogs.
 * @param {string} wsUrl - URL the server listens on.
 * @returns {Object} The mock-socket server instance.
 */
const createAiChatServer = (wsUrl) => {
    const server = new Server(wsUrl);

    server.on('connection', socket => {
        console.log('AI Chat: WebSocket connection established');

        socket.on('message', data => {
            console.log('AI Chat: Received client message:', data);

            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data;

                // Handle ping messages with pong response
                if (parsed.type === 'ping') {
                    socket.send(JSON.stringify({ type: 'pong' }));
                    return;
                }

                // If client sends a chat message (has messages array), handle the turn
                if (parsed.messages && Array.isArray(parsed.messages)) {
                    const userMessages = parsed.messages.filter(m => m.role === 'user');
                    const lastUserMsg = userMessages[userMessages.length - 1];
                    const userContent = lastUserMsg?.content || '';
                    console.log(`AI Chat: User prompt received — "${userContent.substring(0, 60)}"`);

                    handleUserMessage(socket, userContent, parsed);
                }
            } catch (err) {
                console.log('AI Chat: Non-JSON message received:', data);
            }
        });

        socket.on('error', error => {
            console.log('AI Chat: WebSocket error:', error);
        });

        socket.on('close', () => {
            console.log('AI Chat: WebSocket connection closed');
            socketSessions.delete(socket);
        });
    });

    server.on('error', error => {
        console.log('AI Chat WebSocket server error:', error);
    });

    return server;
};

/**
 * Initializes the AI Chat WebSocket server.
 * Builds the conversation map from all chat JSON files under data/chat/, starts
 * the mock server, then routes the chat client's socket onto it.
 * @returns {void}
 */
const initAiChatServer = () => {
    // Build conversation map from all chat recordings
    setConversationMap(buildConversationMap());

    const { AI_CHAT_ENDPOINT_PATH } = offlineConfig.aiChat;
    const wsUrl = getAiChatWebSocketUrl(AI_CHAT_ENDPOINT_PATH);

    window.aiChatServerInstance = createAiChatServer(wsUrl);
    // After the server, so the constructor being wrapped is mock-socket's rather
    // than the browser's — mock-socket swaps the global in its own constructor.
    uninstallUrlRewrite = installChatSocketUrlRewrite(wsUrl, AI_CHAT_ENDPOINT_PATH);

    console.log('AI Chat WebSocket server initialized at:', wsUrl);
};

/**
 * Shuts down the AI Chat WebSocket server if it is running.
 * @returns {void}
 */
const shutdownAiChatServer = () => {
    if (window.aiChatServerInstance) {
        console.log('Shutting down AI Chat WebSocket server');
        uninstallUrlRewrite?.();
        uninstallUrlRewrite = null;
        window.aiChatServerInstance.close();
        window.aiChatServerInstance = null;
        setConversationMap(null);
        resetActiveReplayId();
        socketSessions.clear();
        console.log('AI Chat WebSocket server shut down');
    }
};

export {
    initAiChatServer,
    shutdownAiChatServer,
    getAiChatWebSocketUrl,
    installChatSocketUrlRewrite
};
